import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lock, CheckCircle2, Users, Building2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../auth/AuthContext';
import { StatusPill } from './MyApplications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JobClosePanel = ({ job, closed, onClosed }) => {
  const { user } = useAuth();
  const isOwner = user?.user_id === job.owner_user_id;
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    fetch(`${API}/jobs/${job.id}/applications`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])).then(setApps).catch(() => {});
  }, [job.id, closed, isOwner]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const confirm = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/jobs/${job.id}/close`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ job_title: job.title, company_name: job.author.name, selected_application_ids: selected }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Oferta cerrada · ${data.notified} candidatos avisados (${data.selected} seleccionado/s)`);
      setOpen(false);
      onClosed?.();
    } catch { toast.error('No se pudo cerrar la oferta'); } finally { setSaving(false); }
  };

  if (!isOwner) return null;

  return (
    <div className="sc-card p-6 md:p-8 mb-8 border-dashed" data-testid="job-close-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-amber-500" />
          <h3 className="sc-mono-eyebrow text-amber-500">Panel de empresa · gestión de la oferta</h3>
          <span data-testid="job-close-chat-note" className="text-[10px] text-amber-500">Selección con aviso por chat</span>
        </div>
        <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Users size={11} /> {apps.length} candidaturas recibidas</span>
      </div>
      {closed ? (
        <div className="flex items-center gap-2 text-sm text-slate-300" data-testid="job-close-panel-closed"><CheckCircle2 size={15} className="text-emerald-500" /> Oferta cerrada. Todos los candidatos han sido avisados individualmente.</div>
      ) : (
        <>
          <p className="text-sm text-slate-400 leading-relaxed mb-4">Cuando hayas escogido al personal, confirma el cierre: la oferta pasará a «Completa», nadie más podrá postular y cada candidato recibirá un aviso individual (los seleccionados, uno distinto).</p>
          <button type="button" data-testid="job-close-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 border border-red-500/50 text-red-300 hover:bg-red-500/10 font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors">
            <Lock size={14} /> Personal escogido · cerrar oferta
          </button>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121418] border-[#2A2F3D] text-white max-w-lg" data-testid="job-close-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Cerrar «{job.title}»</DialogTitle>
            <DialogDescription className="text-slate-400">Marca a quién has seleccionado. El resto recibirá el aviso de oferta cubierta.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 my-2">
            {apps.length === 0 && <p className="text-sm text-slate-500 text-center py-4" data-testid="job-close-no-apps">Sin candidaturas todavía. Puedes cerrar la oferta igualmente.</p>}
            {apps.map((a) => (
              <label key={a.id} data-testid={`close-app-${a.id}`} className={`flex items-center gap-3 sc-card p-3 cursor-pointer ${selected.includes(a.id) ? 'border-emerald-500/60 bg-emerald-500/5' : ''}`}>
                <input type="checkbox" className="accent-emerald-500" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
                <div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{a.user_name}</div><div className="text-[11px] text-slate-500 font-mono">{new Date(a.created_at).toLocaleString('es-ES')}</div></div>
                <StatusPill status={a.status} />
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-white px-4 py-2.5">Cancelar</button>
            <button type="button" data-testid="job-close-confirm" onClick={confirm} disabled={saving} className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors"><Lock size={14} /> {saving ? 'Cerrando…' : `Confirmar cierre (${selected.length} seleccionado/s)`}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobClosePanel;
